/**
 * @file auth.js
 * @description Token管理服务，负责Token的获取、刷新和错误处理
 * @author 优化开发团队
 */

import axios from 'axios'
import dayjs from 'dayjs'
import { Toast } from 'vant'
import util from '@/libs/util'
import clientConfig from '@/config/clientConfig' // 使用clientConfig

// 控制并发刷新标志，防止多个请求同时刷新Token
let isRefreshing = false 
// 请求队列，存储等待Token刷新的请求
const requestQueue = []
// 最大重试次数
const MAX_RETRY_COUNT = 3
// 当前重试次数
let retryCount = 0

// 新增: 全局 Token 初始化状态管理
let tokenInitializing = false;
let tokenInitialized = false;
let tokenInitPromise = null;

// Token请求单例控制
let tokenRequestInstance = null;
let tokenRequestPromise = null;



/**
 * 获取Token - 严格单例版
 * 确保在整个应用生命周期内只有一个活跃的token请求
 */
export function getTokenSingleton(callerInfo = '') {
  console.log(`getTokenSingleton 被调用，来源: ${callerInfo}`);
  
  // 如果已有请求实例且未完成，复用该实例
  if (tokenRequestInstance) {
    console.log('复用现有Token请求');
    return tokenRequestPromise;
  }
  
  // 创建新的请求实例
  console.log('创建新的Token请求实例');
  tokenRequestInstance = {};
  tokenRequestPromise = new Promise(async (resolve, reject) => {
    try {
      const result = await refreshToken(`getTokenSingleton:${callerInfo}`);
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      // 请求完成后，重置实例，允许下次请求
      tokenRequestInstance = null;
    }
  });
  
  return tokenRequestPromise;
}

/**
 * 初始化Token服务 - 优化版，避免重复初始化
 * @description 检查Token状态并在需要时进行刷新，设置定时检查
 * @return {Promise<boolean>} 初始化是否成功
 */
export async function initTokenService(callerInfo = '') {
  // 添加调用位置信息
  console.log(`=== initTokenService 被调用 ===`);
  console.log(`调用来源: ${callerInfo}`);
  console.log(`调用时间: ${new Date().toISOString()}`);
  
  // 如果已初始化完成，直接返回true
  if (tokenInitialized) {
    console.log('Token已初始化，跳过重复初始化');
    return true;
  }
  
  // 如果正在初始化中，返回初始化Promise
  if (tokenInitializing) {
    console.log('Token正在初始化中，等待完成');
    return tokenInitPromise;
  }
  
  // 开始初始化流程
  console.log('开始Token初始化流程');
  tokenInitializing = true;
  tokenInitPromise = new Promise(async (resolve) => {
    try {
      const token = util.cookies.get('Access_Token')
      const expiresTime = util.cookies.get('Access_Token_Expires_Time')
      
      // 如果缓存中有有效token，且有效期大于5分钟，直接使用
      if (token && expiresTime) {
        const expiresIn = dayjs(expiresTime).diff(dayjs(), 'minute')
        
        if (expiresIn > 5) {
          console.log('使用缓存的token，剩余有效时间:', expiresIn, '分钟');
          // 仍然设置定时检查，但不立即刷新
          setTokenRefreshTimer();
          tokenInitialized = true;
          resolve(true);
          return;
        }
      }
      
      // 使用单例模式获取Token
      const refreshResult = await getTokenSingleton(`initTokenService:${callerInfo}`);
      
      // 启动定时检查
      setTokenRefreshTimer();
      
      tokenInitialized = true;
      resolve(refreshResult ? true : false);
    } catch (error) {
      console.error('Token初始化失败:', error);
      tokenInitialized = false; // 失败时重置状态，允许重试
      resolve(false);
    } finally {
      tokenInitializing = false;
    }
  });
  
  return tokenInitPromise;
}

/**
 * 设置Token定时刷新
 * @description 定期检查Token是否需要刷新
 */
export function setTokenRefreshTimer() {
  // 清除可能存在的旧定时器
  if (window.tokenRefreshTimer) {
    clearInterval(window.tokenRefreshTimer)
  }
  
  // 设置新的定时器，每5分钟检查一次
  window.tokenRefreshTimer = setInterval(async () => {
    try {
      const expiresTime = util.cookies.get('Access_Token_Expires_Time')
      if (!expiresTime || dayjs(expiresTime).diff(dayjs(), 'minute') <= 3) {
        await refreshToken()
      }
    } catch (error) {
      console.error('定时刷新Token失败:', error)
    }
  }, 5 * 60 * 1000)
  
  // 确保页面关闭时清除定时器
  window.addEventListener('beforeunload', () => {
    if (window.tokenRefreshTimer) {
      clearInterval(window.tokenRefreshTimer)
    }
  })
}

/**
 * 刷新Token - 优化版，增强错误处理和日志
 * @description 获取新的Access Token并存储到cookies中
 * @returns {Promise} 返回包含token的data对象
 */
export async function refreshToken(callerInfo = '') {
  // 添加调用位置信息
  console.log(`=== refreshToken 被调用 ===`);
  console.log(`调用来源: ${callerInfo}`);
  console.log(`调用时间: ${new Date().toISOString()}`);
  console.log(`调用栈: ${new Error().stack}`);
  
  // 如果已经在刷新，则直接返回一个Promise
  if (isRefreshing) {
    console.log('Token已在刷新中，等待完成');
    return new Promise((resolve) => {
      requestQueue.push(resolve);
    });
  }
  
  isRefreshing = true;
  
  try {
    // 从clientConfig获取appid和app_secret
    let appid, app_secret;
    let configSource = '';
    
    try {
      // 优先使用clientConfig的配置
      const authConfig = clientConfig.getAuthConfig();
      appid = authConfig.appid;
      app_secret = authConfig.app_secret;
      configSource = 'clientConfig';
      console.log('使用客户端配置文件获取Token参数');
    } catch (err) {
      // 回退到localStorage
      console.log('客户端配置获取失败，使用localStorage');
      appid = util.localStorage.get('appid');
      app_secret = util.localStorage.get('app_secret');
      configSource = 'localStorage';
      
      // 如果还是没有，尝试从动态配置文件获取
      if (!appid || !app_secret) {
        console.log('localStorage无参数，尝试从配置文件获取');
        const configRes = await getConfigJson();
        appid = configRes.appid;
        app_secret = configRes.app_secret;
        configSource = 'configFile';
      }
    }
    
    console.log(`成功获取认证参数，来源: ${configSource}`);
    
    // 存储到localStorage以备后用
    util.localStorage.set('appid', appid);
    util.localStorage.set('app_secret', app_secret);
    
    // 请求新Token
    console.log('发起Token获取请求', {appid, type: 'stdaln'});
    const response = await axios({
      url: process.env.NODE_ENV === 'development' 
        ? `${location.origin}/api/tccc/secu_ctrl/client_side/get_access_token/`
        : `${location.origin}/tccc/secu_ctrl/client_side/get_access_token/`,
      method: 'get',
      params: {
        appid,
        app_secret,
        type: 'stdaln',
        expires_secs: 10 * 60, // 10分钟
        need_cache: 0
      }
    });
    
    // 获取返回数据
    const res = response.data;
    
    // 根据实际返回结构处理
    if (res && res.status_code === 0) {
      const seconds = 6 * 60; // 6分钟
      // 存储Token - 注意token在data字段内
      util.cookies.set('Access_Token', res.data.token, seconds);
      util.cookies.set('appid', appid, seconds);
      
      // 设置过期时间 - 使用返回的refresh_time字段
      const expires = new Date(res.refresh_time || (new Date() * 1 + seconds * 1000));
      util.cookies.set('Access_Token_Expires_Time', expires, seconds);
      
      console.log('成功获取Token', {
        token: `${res.data.token.substr(0, 10)}...`,
        expiresAt: expires
      });
      
      // 重置重试计数
      retryCount = 0;
      
      // 处理队列中的请求
      requestQueue.forEach(resolve => resolve(res.data));
      requestQueue.length = 0;
      
      return res.data; // 返回包含token的data对象
    } else {
      throw new Error(res?.message || '获取Token失败');
    }
  } catch (error) {
    // 重试逻辑
    if (retryCount < MAX_RETRY_COUNT) {
      retryCount++;
      console.warn(`Token获取失败，第${retryCount}次重试，错误:`, error.message);
      
      // 延迟重试，避免立即失败
      await new Promise(resolve => setTimeout(resolve, 1000));
      return refreshToken();
    } else {
      // 重置计数
      retryCount = 0;
      // 通知队列中的请求失败
      requestQueue.forEach(resolve => resolve(null));
      requestQueue.length = 0;
      
      console.error('Token获取失败，已达到最大重试次数', error);
      throw error;
    }
  } finally {
    isRefreshing = false;
  }
}

/**
 * 获取配置文件 - 改为使用clientConfig
 * @description 优先使用clientConfig配置，加速页面加载
 * @returns {Promise} 配置文件内容
 */
async function getConfigJson() {
  try {
    // 优先使用clientConfig
    try {
      const config = clientConfig.getClientConfig()
      if (config && config.appid && config.app_secret) {
        console.log('使用clientConfig获取配置')
        return config
      }
    } catch (err) {
      console.warn('clientConfig获取失败，使用网络请求')
    }
    
    // 回退到动态获取
    let url = ''
    if (process.env.NODE_ENV !== 'development') {
      url = `${location.origin}/h5/config.json?random=${Math.random()}`
    } else {
      url = `${location.origin}/config.json?random=${Math.random()}`
    }
    
    const response = await axios.get(url)
    return response.data
  } catch (error) {
    console.error('获取配置文件失败:', error)
    throw error
  }
}

/**
 * 处理Token错误
 * @description 检查是否是Token相关错误，如果是则刷新Token
 * @param {Error} error - 请求错误对象
 * @returns {Promise<boolean>} 是否是Token错误并已处理
 */
export function handleTokenError(error) {
  // 检查是否为token相关错误
  const responseData = error.response?.data
  
  if (responseData && 
      (responseData.status_code === -10009 || // Token验证失败的状态码
       responseData.message === 'ACCESS_TOKEN凭证验证失败。')) {
    
    return refreshToken().then(() => true)
  }
  
  return Promise.resolve(false)
}

/**
 * 清除Token信息
 * @description 清除所有Token相关的cookie
 */
export function clearTokenInfo() {
  util.cookies.remove('Access_Token')
  util.cookies.remove('Access_Token_Expires_Time')
  util.cookies.remove('appid')
}

// 新增: 检查并确保认证头部可用
export async function ensureAuthHeaders() {
  // 确保Token已初始化
  await initTokenService();
  
  // 从cookie获取认证信息
  const appid = util.cookies.get('appid');
  const access_token = util.cookies.get('Access_Token');
  
  if (!appid || !access_token) {
    console.warn('认证信息不完整，尝试刷新Token');
    await refreshToken();
  }
  
  // 再次获取认证信息
  const finalAppid = util.cookies.get('appid');
  const finalToken = util.cookies.get('Access_Token');
  
  return {
    appid: finalAppid ? window.decodeURIComponent(finalAppid) : '',
    access_token: finalToken ? window.decodeURIComponent(finalToken) : '',
    type: 'stdaln',
    force_check: 'true'
  };
}

// 导出
export default {
  initTokenService,
  refreshToken,
  handleTokenError,
  clearTokenInfo,
  setTokenRefreshTimer,
  ensureAuthHeaders
} 